import React, { useCallback, useContext, useState } from 'react'
import GridSelectField from '../../components/survey/inputs/grid-select-field'

import { SurveyInstance, SurveyQuestionShape } from 'src/services/survey'
import TextInputField from 'src/components/survey/inputs/text-input-field'
import TextAreaField from 'src/components/survey/inputs/text-area-field'
import NumberInputField from 'src/components/survey/inputs/number-input-field'
import DateInputField from 'src/components/survey/inputs/date-input-field'
import DropDownField from 'src/components/survey/inputs/dropdown-field'
import PhoneInputField from 'src/components/survey/inputs/phone-input-field'
import CheckboxField from 'src/components/survey/inputs/checkbox-field'
import FileUploadField from 'src/components/survey/inputs/file-upload-field'
import AddressField from 'src/components/survey/inputs/address-field'

interface SurveyContextProviderProps {
  responseRef: any
  setResponseRef: any
  response: any
  setResponse: (response: any) => void
  componentType: (question: SurveyQuestionShape) => any
  instance: SurveyInstance | null
  setInstance: (instance: SurveyInstance) => void
  instanceId: string | null | undefined
  setInstanceId: (instanceId: string) => void
  setCurrentSection?: (section: number) => void
  currentSection?: number
  /** True while a file answer is uploading; survey navigation waits for it. */
  isUploading: boolean
  setIsUploading: (isUploading: boolean) => void
}

export const SurveyContext = React.createContext<SurveyContextProviderProps>({
  responseRef: null,
  setResponseRef: null,
  response: null,
  setResponse: (_response: any) => {},
  componentType: (_question: SurveyQuestionShape) => {},
  instance: null,
  setInstance: (_instance: SurveyInstance) => {},
  instanceId: null,
  setInstanceId: (_instanceId: string) => {},
  setCurrentSection: (_section: number) => {},
  currentSection: 0,
  isUploading: false,
  setIsUploading: (_isUploading: boolean) => {},
})

export const useSurveyForm = () => {
  const context = useContext(SurveyContext)
  if (!context) {
    throw new Error('useSurveyForm must be used within a SurveyProvider')
  }
  return context
}

export function SurveyProvider({ children }: { children: any }) {
  const [response, setResponse] = useState<any>('')
  const [responseRef, setResponseRef] = useState<any>(null)
  const [instance, setInstance] = useState<SurveyInstance | null>(null)
  const [instanceId, setInstanceId] = useState<string | null | undefined>('')
  const [currentSection, setCurrentSection] = useState<number>(0)
  const [isUploading, setIsUploading] = useState(false)
  const componentType = useCallback((question: SurveyQuestionShape) => {
    // Preferred: component_type based mapping (mirrors upstream tn_surveys).
    if (question.componentType && question.componentType !== 'NONE') {
      const componentTypeMap: Record<string, any> = {
        DROPDOWN: DropDownField,
        CHECKBOX: CheckboxField,
        RADIO_BUTTON: GridSelectField,
        TEXT_INPUT: TextInputField,
        TEXT_AREA: TextAreaField,
        NUMBER_INPUT: NumberInputField,
        DATE_PICKER: DateInputField,
        FILE_UPLOAD: FileUploadField,
        ADDRESS: AddressField,
        PHONE_INPUT: PhoneInputField,
      }
      return componentTypeMap[question.componentType]
    }

    // Fallback: legacy questionDisplay:questionType mapping for unmigrated questions.
    const questionTypeMap = {
      'OPEN_TEXT:NUMBER': TextInputField,
      'OPEN_TEXT:TEXT': TextInputField,
      'DROPDOWN:SINGLE_OPTION': DropDownField,
      'LIST_VERTICAL:SINGLE_OPTION': GridSelectField,
      'LIST_HORIZONTAL:SINGLE_OPTION': GridSelectField,
      'GRID_LAYOUT:MULTIPLE_OPTION': GridSelectField,
      'GRID_LAYOUT:SINGLE_OPTION': GridSelectField,
      'LIST_VERTICAL:MULTIPLE_OPTION': GridSelectField,
      'DATE:TEXT': DateInputField,
      'PHONE_INPUT:TEXT': PhoneInputField,
    }
    return questionTypeMap[
      `${question.questionDisplay}:${question.questionType}` as keyof typeof questionTypeMap
    ]
  }, [])

  return (
    <SurveyContext.Provider
      value={{
        responseRef,
        setResponseRef,
        response,
        setResponse,
        componentType,
        instance,
        setInstance,
        instanceId,
        setInstanceId,
        setCurrentSection,
        currentSection,
        isUploading,
        setIsUploading,
      }}
    >
      {children}
    </SurveyContext.Provider>
  )
}
